import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import API_BASE_URL from '../config';

export type ModeType = 'home' | 'kitchen' | 'professional';

interface ModeContextType {
  activeMode: ModeType;
  setActiveMode: (mode: ModeType) => void;
  groceryCollectionId: number | null;
  ensureGroceryCollection: (token: string, spaceId?: number) => Promise<number | null>;
  resetGroceryCollection: () => void;
}

const ModeContext = createContext<ModeContextType | undefined>(undefined);

export const useMode = () => {
  const context = useContext(ModeContext);
  if (context === undefined) {
    throw new Error('useMode must be used within a ModeProvider');
  }
  return context;
};

const MODE_KEY = 'app-activeMode';
const GROCERY_COLLECTION_KEY_PREFIX = 'mode-grocery-collectionId';
const ACTIVE_SPACE_KEY = 'app-activeSpaceId';

function groceryKey(spaceId?: number | null): string {
  return spaceId ? `${GROCERY_COLLECTION_KEY_PREFIX}-${spaceId}` : GROCERY_COLLECTION_KEY_PREFIX;
}
const COLLECTION_KEY = 'inventory-collection';

export const ModeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [activeMode, setActiveModeState] = useState<ModeType>(() => {
    // Try new key first, then fall back to old key for migration
    const saved = localStorage.getItem(MODE_KEY) || localStorage.getItem('app-activeSpace');
    if (saved === 'kitchen' || saved === 'professional') return saved as ModeType;
    return 'home';
  });

  const [groceryCollectionId, setGroceryCollectionId] = useState<number | null>(() => {
    const spaceId = localStorage.getItem(ACTIVE_SPACE_KEY);
    const key = groceryKey(spaceId ? parseInt(spaceId, 10) : null);
    const saved = localStorage.getItem(key);
    return saved ? parseInt(saved, 10) : null;
  });

  const setActiveMode = useCallback((mode: ModeType) => {
    const previousMode = activeMode;
    
    // Update local state immediately
    setActiveModeState(mode);
    localStorage.setItem(MODE_KEY, mode);
    
    // Reset collection to "All Items" when switching away from Kitchen mode
    // if the current collection is a grocery collection
    if (previousMode === 'kitchen' && mode !== 'kitchen' && groceryCollectionId) {
      const currentCollection = localStorage.getItem(COLLECTION_KEY);
      if (currentCollection === String(groceryCollectionId)) {
        console.log('[ModeContext] Switching away from Kitchen mode, resetting collection from grocery to All Items');
        localStorage.setItem(COLLECTION_KEY, 'all');
        // Dispatch custom event to notify InventoryView of collection change
        window.dispatchEvent(new CustomEvent('collection-reset', { detail: { collection: 'all' } }));
      }
    }
  }, [activeMode, groceryCollectionId]);

  const resetGroceryCollection = useCallback(() => {
    console.log('[ModeContext] Clearing grocery collection (space changed)');
    setGroceryCollectionId(null);
  }, []);

  const ensureGroceryCollection = useCallback(async (token: string, spaceId?: number): Promise<number | null> => {
    try {
      console.log(`[ModeContext] Looking for grocery collection in space ${spaceId || 'default'}...`);
      const url = spaceId 
        ? `${API_BASE_URL}/api/collections?include_counts=false&space_id=${spaceId}`
        : `${API_BASE_URL}/api/collections?include_counts=false`;
        
      const collectionsRes = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (collectionsRes.ok) {
        const data = await collectionsRes.json();
        const cols = data.collections || [];
        console.log(`[ModeContext] Found ${cols.length} collections, types: ${cols.map((c: any) => `${c.name}(${c.collection_type})`).join(', ')}`);
        const existing = cols.find(
          (c: any) => c.collection_type === 'grocery'
        );
        if (existing) {
          console.log(`[ModeContext] Found grocery collection: ${existing.id} - ${existing.name}`);
          setGroceryCollectionId(existing.id);
          localStorage.setItem(groceryKey(spaceId), String(existing.id));
          return existing.id;
        } else {
          console.log('[ModeContext] No grocery collection found, will create one');
        }
      } else {
        console.error(`[ModeContext] Failed to fetch collections: ${collectionsRes.status}`);
      }

      const createRes = await fetch(`${API_BASE_URL}/api/collections`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          name: 'Grocery', 
          collection_type: 'grocery',
          space_id: spaceId
        })
      });
      if (createRes.ok) {
        const newCol = await createRes.json();
        console.log(`[ModeContext] Created grocery collection: ${newCol.id} - ${newCol.name}`);
        setGroceryCollectionId(newCol.id);
        localStorage.setItem(groceryKey(spaceId), String(newCol.id));
        return newCol.id;
      } else {
        // Creation failed (409 duplicate, 500 server error, etc.)
        // Re-fetch — the collection may have been created despite the error response
        const errBody = await createRes.text();
        console.warn(`[ModeContext] Create returned ${createRes.status}: ${errBody}`);
        const retryRes = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
        if (retryRes.ok) {
          const retryData = await retryRes.json();
          const found = (retryData.collections || []).find(
            (c: any) => c.collection_type === 'grocery' || c.name === 'Grocery'
          );
          if (found) {
            console.log(`[ModeContext] Found grocery collection on retry: ${found.id}`);
            setGroceryCollectionId(found.id);
            localStorage.setItem(groceryKey(spaceId), String(found.id));
            return found.id;
          }
        }
      }
    } catch (err) {
      console.error('[ModeContext] Failed to ensure Grocery collection:', err);
    }
    return null;
  }, []);

  return (
    <ModeContext.Provider value={{ activeMode, setActiveMode, groceryCollectionId, ensureGroceryCollection, resetGroceryCollection }}>
      {children}
    </ModeContext.Provider>
  );
};
